import { Button, Icon } from '@components';

import DynamicIcon from '../DynamicIcon';
import { BANNER_HEIGHT } from './constants';

import {
  trackDynamicComponentCta,
  trackDynamicComponentDismissed
} from '../../analytics/dynamicComponentMixpanel';
import {
  BannerData,
  BannerResolvedStyles,
  ComponentType,
  ProcessedComponentInstance
} from '../../types';
import sanitizeHtml from '../../utils/sanitizeHtml';

interface DynamicBannerProps {
  instance: ProcessedComponentInstance<BannerData, BannerResolvedStyles>;
  showButton: boolean;
  onAction?: () => void;
  onDismiss: () => void;
}

const DynamicBanner = ({
  instance,
  showButton,
  onAction,
  onDismiss
}: DynamicBannerProps) => {
  const { data, resolvedStyles } = instance;
  const isDismissible = instance.is_dismissible;
  const isStaticBanner = instance.is_frontend_static_banner;
  const { title, description, button } = data;
  const { bgColor, titleColor, descriptionColor, buttonBgColor } =
    resolvedStyles;
  const iconUrl = resolvedStyles.icon_url;

  const shouldTrack = !isStaticBanner;

  const handleAction = () => {
    if (shouldTrack && button) {
      trackDynamicComponentCta(button, instance, ComponentType.BANNER);
    }
    onAction?.();
  };

  const handleDismiss = () => {
    if (shouldTrack) {
      trackDynamicComponentDismissed(
        instance,
        ComponentType.BANNER,
        'Banner close button'
      );
    }
    onDismiss();
  };

  return (
    <div
      className="flex w-full items-center justify-between p-6"
      style={{ height: BANNER_HEIGHT, backgroundColor: bgColor }}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <DynamicIcon
            name={iconUrl || 'info-circle'}
            color={buttonBgColor}
            size="24"
            className="shrink-0"
          />
          {title && (
            <div
              className="text-lg font-semibold"
              style={{ color: titleColor }}
            >
              {title}
            </div>
          )}
        </div>
        <div
          className="break-words pl-9 text-base"
          style={{ color: descriptionColor }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
        />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {showButton && button && onAction && (
          <div style={{ '--btn-bg': buttonBgColor } as React.CSSProperties}>
            <Button
              variant="primary"
              size="small"
              label={button.text}
              handleClick={handleAction}
              className="!bg-[var(--btn-bg)]"
              labelStyle="!text-theme"
            />
          </div>
        )}

        {isDismissible && (
          <Button
            variant="transparent"
            handleClick={handleDismiss}
            className="!p-0.5 opacity-60 hover:opacity-100"
            aria-label="Dismiss banner"
          >
            <Icon name="close" size="large" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DynamicBanner;
